import { Backdrop, Box, Button, CircularProgress } from '@mui/material';
import {
  HEADER_HEIGHT,
  LOADING_BACKDROP_STYLE,
} from '../../_constants/styleConst';
import React, { useState } from 'react';
import { FileDownloadOutlined, FileUploadOutlined } from '@mui/icons-material';
import ErrorModal from '../../_common/Modal/ErrorModal';
import FileImportModal from '../../_common/Modal/FileImportModal';
import { useTranslation } from 'react-i18next';
import useErrorModal from '../../hooks/useErrorModal';
import {
  CRITERIA_EXCEL_FILE_NAME,
  CRITERIA_SHEET_NAME,
  IMPORTABLE_EXCEL_FORMAT_LIST,
} from '../../_constants/manageConst';
import XLSX from 'xlsx';
import {
  deleteCriteriaMaster,
  getCriteriaMaster,
  postCriteriaMaster,
  putCriteriaMaster,
} from '../../services/api/criteriaMaster';
import { isGetItemModelCriteriaMaster } from '../../_utils/typeGuardUtils';
import { API_CODE } from '../../_constants/apiConst';
import {
  convertInputDataToCorrectType,
  createExportExcelName,
  hasUndefinedMember,
  isEmptyObject,
  isEqualNullable,
} from '../../_utils/utils';
import { GetItemModelCriteria } from '../../services/apiTypes';
import { RootState } from '../../stores/store';
import { useSelector } from 'react-redux';

type CriteriaRaw = {
  modelName: string;
  itemId: string;
  text: string | null;
  max: number | null;
  min: number | null;
};

const ManageCriteria: React.FC = () => {
  const { t } = useTranslation();
  const {
    error,
    showApiError,
    showTypeError,
    showClientError,
    hideError,
    getApiErrorMessage,
    getTypeErrorMessage,
  } = useErrorModal();
  const { userData } = useSelector((state: RootState) => state.userInfo);
  const [isLoading, setIsLoading] = useState(false);
  /** インポートするファイル */
  const [file, setFile] = useState<File | undefined>();
  /** インポートするファイルを選択するモーダル */
  const [isOpenImportModal, setIsOpenImportModal] = useState(false);
  /** インポートのステータス (0: 未処理, 1: 処理中, 2: 完了, 3: エラー, 4: 完了(変更なし)) */
  const [importStatus, setImportStatus] = useState<0 | 1 | 2 | 3 | 4>(0);
  /** インポートモーダル用エラーメッセージ */
  const [errorMsg, setErrorMsg] = useState<string>('');

  /** 診断基準のインポート処理 */
  const doImportCriteria = async () => {
    setErrorMsg('');
    setImportStatus(1);
    // ファイルが未選択
    if (!file) {
      setImportStatus(3);
      setErrorMsg(t('ERR_IMPORT'));
      return;
    }
    // インポートできないフォーマットのファイル
    if (!IMPORTABLE_EXCEL_FORMAT_LIST.includes(file.type)) {
      setImportStatus(3);
      setErrorMsg(t('ERR_IMPORT'));
      return;
    }

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'buffer', bookVBA: true });
      const criteriaSheet = workbook.Sheets[CRITERIA_SHEET_NAME.CRITERIA];
      // シートが無ければエラーにする
      if (criteriaSheet === undefined) {
        setImportStatus(3);
        setErrorMsg(t('ERR_IMPORT'));
        return;
      }
      const response = await getCriteriaMaster();
      if (!isGetItemModelCriteriaMaster(response)) {
        setImportStatus(3);
        setErrorMsg(getTypeErrorMessage(API_CODE.ITEM_MODEL_CRITERIA_MASTER));
        return;
      }
      const criteriaDataOld: CriteriaRaw[] = response.modelNameList.flatMap(
        (modelName) =>
          response.itemModelCriteriaList
            .filter((item) => item.modelName === modelName)
            .map((itemModelCriteria) => ({
              modelName,
              itemId: itemModelCriteria.itemId,
              min: itemModelCriteria.criteria?.min,
              max: itemModelCriteria.criteria?.max,
              text: itemModelCriteria.criteria?.text,
            })),
      );

      const criteriaDataNew: CriteriaRaw[] = XLSX.utils
        .sheet_to_json<CriteriaRaw>(criteriaSheet, {
          raw: true,
          blankrows: true,
        })
        .map((item) => {
          item.itemId = convertInputDataToCorrectType(item.itemId, 'string');
          item.modelName = convertInputDataToCorrectType(
            item.modelName,
            'string',
          );
          item.text = convertInputDataToCorrectType(item.text, 'string');
          item.max = convertInputDataToCorrectType(item.max, 'number');
          item.min = convertInputDataToCorrectType(item.min, 'number');

          if (hasUndefinedMember(item)) {
            throw new Error('input data error');
          }

          return item;
        })
        .filter(
          (item) =>
            !isEmptyObject(item) &&
            response.modelNameList.includes(item.modelName),
        );

      const { createList, updateList, deleteList } = getCriteriaMasterDiff(
        criteriaDataNew,
        criteriaDataOld,
      );

      if (
        createList.length === 0 &&
        updateList.length === 0 &&
        deleteList.length === 0
      ) {
        setImportStatus(4);
        return;
      }

      for (const item of createList) {
        await postCriteriaMaster({
          modelName: item.modelName,
          itemId: item.itemId,
          criteria: {
            min: item?.min,
            max: item?.max,
            text: item?.text,
          },
        } as GetItemModelCriteria);
      }

      for (const item of updateList) {
        await putCriteriaMaster({
          modelName: item.modelName,
          itemId: item.itemId,
          criteria: {
            min: item?.min,
            max: item?.max,
            text: item?.text,
          },
        } as GetItemModelCriteria);
      }

      for (const item of deleteList) {
        await deleteCriteriaMaster({
          modelName: item.modelName,
          itemId: item.itemId,
          criteria: {
            min: item?.min,
            max: item?.max,
            text: item?.text,
          },
        } as GetItemModelCriteria);
      }

      setImportStatus(2);
      return;
    } catch (error) {
      setImportStatus(3);
      setErrorMsg(
        getApiErrorMessage(error, API_CODE.ITEM_MODEL_CRITERIA_MASTER),
      );
      return;
    }
  };

  /** インポート前との差分を取得する */
  const getCriteriaMasterDiff = (
    newList: CriteriaRaw[],
    oldList: CriteriaRaw[],
  ) => {
    const createList: CriteriaRaw[] = [];
    const updateList: CriteriaRaw[] = [];
    const deleteList: CriteriaRaw[] = [];

    const oldMap = new Map<string, CriteriaRaw>(
      oldList.map((item) => [`${item.modelName}---${item.itemId}`, item]),
    );

    const newMap = new Map<string, CriteriaRaw>(
      newList.map((item) => [`${item.modelName}---${item.itemId}`, item]),
    );

    newMap.forEach((newItem, key) => {
      const oldItem = oldMap.get(key);
      if (!oldItem) {
        createList.push(newItem);
      } else if (
        !isEqualNullable(newItem?.text, oldItem?.text) ||
        !isEqualNullable(newItem?.min, oldItem?.min) ||
        !isEqualNullable(newItem?.max, oldItem?.max)
      ) {
        updateList.push(newItem);
      }
    });

    oldMap.forEach((oldItem, key) => {
      if (!newMap.has(key)) {
        deleteList.push(oldItem);
      }
    });

    return { createList, updateList, deleteList };
  };

  /** インポートモーダルのキャンセルボタン押下時 */
  const onClickCancel = () => {
    setIsOpenImportModal(false);
    setImportStatus(0);
    setFile(undefined);
    setErrorMsg('');
  };

  /** 診断基準マスタのエクスポート */
  const doExportCriteriaMaster = async () => {
    setIsLoading(true);
    try {
      const workbook = XLSX.utils.book_new();
      await addCriteriaSheet(workbook);
      XLSX.writeFile(
        workbook,
        createExportExcelName(CRITERIA_EXCEL_FILE_NAME, userData.dateFormat),
        { compression: true },
      );
    } catch (error) {
      if (error instanceof Error && error.message === 'ADD_SHEET') {
        return;
      } else {
        // エクセル操作のエラー
        showClientError('ERR_EXPORT_INSPECTION_CRITERIA');
        return;
      }
    } finally {
      setIsLoading(false);
    }
  };

  /** 診断基準シート追加 */
  const addCriteriaSheet = async (workbook: XLSX.WorkBook): Promise<void> => {
    try {
      const response = await getCriteriaMaster();
      if (!isGetItemModelCriteriaMaster(response)) {
        showTypeError(API_CODE.ITEM_MODEL_CRITERIA_MASTER);
      }

      /** 診断基準データを作成 */
      const criteriaData = response.modelNameList.flatMap((modelName) =>
        response.itemModelCriteriaList
          .filter((item) => item.modelName === modelName)
          .map((itemModelCriteria) => {
            const itemMaster = response.itemMasterList.find(
              (item) => item.itemId === itemModelCriteria.itemId,
            );
            return {
              modelName,
              itemId: itemModelCriteria.itemId,
              itemName: itemMaster?.itemName,
              min: itemModelCriteria.criteria?.min,
              max: itemModelCriteria.criteria?.max,
              text: itemModelCriteria.criteria?.text,
            };
          }),
      );

      /** itemIdをキーにして重複を除去 */
      const itemMap = new Map<string, { itemId?: string; itemName?: string }>();
      for (const item of criteriaData) {
        if (!item.itemId || itemMap.has(item.itemId)) continue;
        itemMap.set(item.itemId, {
          itemId: item.itemId,
          itemName: item.itemName,
        });
      }

      /** 配列としてソートを実装 */
      const itemSortData = Array.from(itemMap.values()).sort((a, b) => {
        const aParts = (a.itemId ?? '').split('-').map(Number);
        const bParts = (b.itemId ?? '').split('-').map(Number);
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aVal = aParts[i] ?? 0;
          const bVal = bParts[i] ?? 0;
          if (aVal !== bVal) return aVal - bVal;
        }
        return 0;
      });

      /** シートに変換 */
      const sheet1 = XLSX.utils.json_to_sheet(criteriaData);
      const sheet2 = XLSX.utils.json_to_sheet(itemSortData);

      /** sheet1の列数を取得し、sheet2を右側に1列空けて配置 */
      const sheet1ColumnCount =
        XLSX.utils.decode_range(sheet1['!ref'] as string).e.c + 1;
      const offsetColumn = sheet1ColumnCount + 1;
      for (const cell in sheet2) {
        if (cell[0] === '!') continue;
        const cellAddress = XLSX.utils.decode_cell(cell);
        const newAddress = {
          r: cellAddress.r,
          c: cellAddress.c + offsetColumn,
        };
        const newCellRef = XLSX.utils.encode_cell(newAddress);
        sheet1[newCellRef] = sheet2[cell];
      }
      const sheet1Range = XLSX.utils.decode_range(sheet1['!ref'] as string);
      const sheet2Range = XLSX.utils.decode_range(sheet2['!ref'] as string);
      sheet1['!ref'] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: {
          r: Math.max(sheet1Range.e.r, sheet2Range.e.r),
          c: offsetColumn + sheet2Range.e.c,
        },
      });

      /** 診断基準シート */
      XLSX.utils.book_append_sheet(
        workbook,
        sheet1,
        CRITERIA_SHEET_NAME.CRITERIA,
      );

      /** モデル選択用リストシート */
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(
          response.modelNameList.map((item) => ({ modelName: item })),
        ),
        CRITERIA_SHEET_NAME.MODEL_NAME_LIST,
      );

      /** 項目選択用リスト */
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(response.itemMasterList),
        CRITERIA_SHEET_NAME.ITEM_MASTER_LIST,
      );
    } catch (error) {
      showApiError(error, API_CODE.ITEM_MODEL_CRITERIA_MASTER);
      throw new Error('ADD_SHEET');
    }
  };

  return (
    <Box
      sx={{ marginTop: `calc(${HEADER_HEIGHT} + 2rem)`, marginLeft: '3rem' }}
    >
      <Box
        sx={{ display: 'flex', justifyContent: 'flex-start', flexFlow: 'wrap' }}
      >
        <Box fontSize="1.5rem" sx={{ minWidth: '20%' }}>
          {t('LBL_CRITERIA_MASTER')}
        </Box>
        <Box
          sx={{
            display: 'flex',
            ml: '1.5rem',
            minWidth: '20%',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Button
              variant="contained"
              sx={{ backgroundColor: '#1E83BE', minWidth: '150px' }}
              onClick={() => setIsOpenImportModal(true)}
              fullWidth
            >
              <FileDownloadOutlined sx={{ fontSize: '1.3rem' }} />
              {t('BTN_IMPORT')}
            </Button>
          </Box>
          <Box>
            <Button
              variant="contained"
              onClick={() => doExportCriteriaMaster()}
              fullWidth
              sx={{
                backgroundColor: '#E0356C ',
                minWidth: '150px',
                ml: '1rem',
              }}
            >
              <FileUploadOutlined sx={{ fontSize: '1.3rem' }} />
              {t('BTN_EXPORT')}
            </Button>
          </Box>
        </Box>
      </Box>
      <FileImportModal
        isOpen={isOpenImportModal}
        handleClose={onClickCancel}
        onClickOk={doImportCriteria}
        acceptableFiles={IMPORTABLE_EXCEL_FORMAT_LIST}
        setFile={setFile}
        file={file}
        status={importStatus}
        errorMsg={errorMsg}
      />
      <Backdrop
        sx={{
          ...LOADING_BACKDROP_STYLE,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {error && <ErrorModal message={error} onClose={hideError} />}
    </Box>
  );
};

export default ManageCriteria;
